import { Box, Button, ButtonGroup, Flex, Text, chakra } from "@chakra-ui/react";
import { useCallback, useState } from "react";
import { isEmail } from "validator";
import { GenderPreference, SparkRegisterInfo } from "@greenflagdate/shared";
import { BirthDateInput } from "../../../components/BirthDateInput/BirthDateInput";
import { FormField } from "../../../components/FormField/FormField";
import { FormInput } from "../../../components/FormInput/FormInput";
import { PhoneField } from "../../PhoneField/PhoneField";
import { GenderSelector } from "../../GenderSelector/GenderSelector";
import { Icon } from "../../Icon/Icon";
import { TextNotificationOptIn } from "../../TextNotificationOptIn/TextNotificationOptIn";
import { getPhoneUtil } from "../../../lib/phoneUtil";

interface ProfileInfoStepProps {
  sparkRegisterInfo: SparkRegisterInfo;
  omitFields?: (keyof SparkRegisterInfo)[];
  onChangeRegisterInfo: (val: Partial<SparkRegisterInfo>) => void;
  // Text-notification opt-in shown under the phone field.
  textOptIn: boolean;
  onChangeTextOptIn: (checked: boolean) => void;
  onSubmitSuccess: (val: Partial<SparkRegisterInfo>) => void;
  buttonLabel: string;
  isLoading?: boolean;
  policiesAccepted?: boolean;
  policyCheckboxes?: React.ReactNode;
  onPolicyError?: () => void;
}

type Errors = Partial<Record<keyof SparkRegisterInfo, string>>;

export const ProfileInfoStep = ({
  sparkRegisterInfo,
  omitFields,
  onChangeRegisterInfo,
  textOptIn,
  onChangeTextOptIn,
  onSubmitSuccess,
  buttonLabel,
  isLoading,
  policiesAccepted,
  policyCheckboxes,
  onPolicyError,
}: ProfileInfoStepProps) => {
  const [errors, setErrors] = useState<Errors>({});
  const submit = useCallback(async () => {
    const errs: Errors = {};
    if (!omitFields?.includes("first_name") && !sparkRegisterInfo.first_name) {
      errs.first_name = "Please enter your first name";
    }
    if (!omitFields?.includes("email")) {
      if (!sparkRegisterInfo.email) {
        errs.email = "Please enter your email";
      } else if (!isEmail(sparkRegisterInfo.email)) {
        errs.email = "Please double check your email";
      }
    }
    if (!omitFields?.includes("birth_date") && !sparkRegisterInfo.birth_date) {
      errs.birth_date = "Please enter your birth date";
    }
    if (sparkRegisterInfo.birth_date) {
      const date = new Date(sparkRegisterInfo.birth_date);
      const validDate = !isNaN(date.getTime()) && date.getFullYear() !== 0;
      if (!validDate) {
        errs.birth_date = "Please confirm that your birth date is correct";
      }
    }
    if (!omitFields?.includes("gender") && !sparkRegisterInfo.gender) {
      errs.gender = "Please enter your gender";
    }
    if (
      !omitFields?.includes("gender_preference") &&
      !sparkRegisterInfo.gender_preference &&
      // Friends only is a complete answer on its own.
      !sparkRegisterInfo.friends_only
    ) {
      errs.gender_preference =
        "Choose who you want to date, or pick friends only";
    }
    if (!omitFields?.includes("phone_number_to_validate")) {
      if (!sparkRegisterInfo.phone_number_to_validate) {
        errs.phone_number_to_validate = "Please enter your phone number";
      } else {
        const { phoneUtil } = await getPhoneUtil();
        let parsed;
        try {
          parsed = phoneUtil.parse(
            sparkRegisterInfo.phone_number_to_validate,
            "US"
          );
        } catch {
          errs.phone_number_to_validate =
            "It looks like this phone number is invalid";
        }
        if (parsed && !phoneUtil.isValidNumberForRegion(parsed, "US")) {
          errs.phone_number_to_validate =
            "It looks like this phone number is invalid";
        }
      }
    }
    setErrors(errs);
    if (policiesAccepted === false) onPolicyError?.();
    if (!Object.keys(errs).length && policiesAccepted !== false) {
      onSubmitSuccess(sparkRegisterInfo);
    }
  }, [omitFields, onSubmitSuccess, sparkRegisterInfo, policiesAccepted, onPolicyError]);

  return (
    // Column-fill so the inner footer can pin to the bottom of whatever
    // height the parent gives us. The fields scroll inside the flex={1}
    // section; the Continue button stays visible at the bottom of the
    // drawer.
    <Box display="flex" flexDirection="column" flex="1" minH="0">
      <Box
        flex="1"
        minH="0"
        overflowY="auto"
        px="20px"
        pb="16px"
        display="flex"
        flexDirection="column"
        gap="16px"
      >
      {!omitFields?.includes("gender") && (
        <FormField label="What's your gender?" error={errors.gender}>
          <GenderSelector
            value={sparkRegisterInfo.gender}
            onChange={(gender) => onChangeRegisterInfo({ gender })}
          />
        </FormField>
      )}

      {!omitFields?.includes("first_name") && (
        <FormField label="First name" error={errors.first_name}>
          <FormInput
            value={sparkRegisterInfo.first_name || ""}
            placeholder="First name"
            onChange={(e) =>
              onChangeRegisterInfo({ first_name: e.currentTarget.value })
            }
          />
        </FormField>
      )}

      {!omitFields?.includes("email") && (
        <FormField label="Email" error={errors.email}>
          <FormInput
            type="email"
            value={sparkRegisterInfo.email || ""}
            placeholder="you@example.com"
            onChange={(e) =>
              onChangeRegisterInfo({ email: e.currentTarget.value })
            }
          />
        </FormField>
      )}

      {!omitFields?.includes("birth_date") && (
        <FormField label="Birth date" error={errors.birth_date}>
          <BirthDateInput
            value={sparkRegisterInfo.birth_date || ""}
            onChange={(birth_date) => onChangeRegisterInfo({ birth_date })}
          />
        </FormField>
      )}

      {!omitFields?.includes("gender_preference") && (
        <FormField
          label="Who do you want to date?"
          error={errors.gender_preference}
        >
          <ButtonGroup
            attached
            w="100%"
            opacity={sparkRegisterInfo.friends_only ? 0.45 : 1}
          >
            {(
              [
                { value: "man", label: "Men" },
                { value: "woman", label: "Women" },
                { value: "everyone", label: "Everyone" },
              ] as { value: GenderPreference; label: string }[]
            ).map((opt, i, arr) => {
                const active =
                  !sparkRegisterInfo.friends_only &&
                  sparkRegisterInfo.gender_preference?.toLowerCase() ===
                  opt.value;
                const isFirst = i === 0;
                const isLast = i === arr.length - 1;
                return (
                  <Button
                    key={opt.value}
                    type="button"
                    disabled={sparkRegisterInfo.friends_only}
                    flex="1"
                    h="auto"
                    px="18px"
                    py="16px"
                    fontFamily="body"
                    fontSize="15px"
                    fontWeight="semibold"
                    letterSpacing="-0.005em"
                    textTransform="none"
                    borderWidth="1.5px"
                    borderStyle="solid"
                    borderColor={active ? "border.brand" : "border.default"}
                    bg={active ? "bg.brandSoft" : "bg.canvas"}
                    color={active ? "fg.onTint" : "fg.default"}
                    borderTopLeftRadius={isFirst ? "14px" : "0"}
                    borderBottomLeftRadius={isFirst ? "14px" : "0"}
                    borderTopRightRadius={isLast ? "14px" : "0"}
                    borderBottomRightRadius={isLast ? "14px" : "0"}
                    // Active button overlaps the seam so its full
                    // brand-colored border is drawn instead of the neighbor's.
                    ml={!isFirst ? "-1.5px" : undefined}
                    zIndex={active ? 1 : 0}
                    _hover={
                      sparkRegisterInfo.friends_only
                        ? { filter: "none" }
                        : {
                            bg: active ? "bg.brandSoft" : "bg.subtle",
                            filter: "none",
                          }
                    }
                    // The group already carries the greyed-out opacity, so the
                    // button recipe must not dim each option a second time.
                    _disabled={{ opacity: 1, cursor: "not-allowed" }}
                    onClick={() =>
                      onChangeRegisterInfo({
                        gender_preference: opt.value,
                        friends_only: false,
                      })
                    }
                  >
                    {opt.label}
                  </Button>
                );
              })}
          </ButtonGroup>

          {/* Same choice the sign-up flow offers, so someone whose first
              contact with us is an event isn't forced to declare a dating
              preference they don't want. */}
          <chakra.button
            type="button"
            onClick={() =>
              onChangeRegisterInfo({
                friends_only: !sparkRegisterInfo.friends_only,
              })
            }
            display="flex"
            alignItems="flex-start"
            gap="12px"
            w="100%"
            mt="12px"
            p="14px"
            textAlign="left"
            cursor="pointer"
            borderWidth="1.5px"
            borderStyle="solid"
            borderRadius="14px"
            borderColor={
              sparkRegisterInfo.friends_only ? "border.friends" : "border.default"
            }
            bg={sparkRegisterInfo.friends_only ? "bg.friendsSoft" : "bg.canvas"}
            _hover={{
              bg: sparkRegisterInfo.friends_only ? "bg.friendsSoft" : "bg.subtle",
            }}
          >
            <Flex
              mt="1px"
              w="20px"
              h="20px"
              flexShrink={0}
              borderRadius="6px"
              borderWidth="1.5px"
              borderStyle="solid"
              borderColor={
                sparkRegisterInfo.friends_only
                  ? "border.friends"
                  : "border.default"
              }
              bg={sparkRegisterInfo.friends_only ? "bg.friends" : "transparent"}
              align="center"
              justify="center"
            >
              {sparkRegisterInfo.friends_only && (
                <Icon name="check" boxSize="11px" color="fg.onBrand" />
              )}
            </Flex>
            <Box>
              <Text
                fontFamily="body"
                fontSize="15px"
                fontWeight="semibold"
                color="fg.default"
              >
                I'm here for friends, not dating
              </Text>
              <Text
                fontFamily="body"
                fontSize="13px"
                color="fg.muted"
                lineHeight="1.4"
                mt="2px"
              >
                Your profile won't show up for dating. You'll still match as
                friends after events you attend, and you can change this any
                time.
              </Text>
            </Box>
          </chakra.button>
        </FormField>
      )}

      {!omitFields?.includes("phone_number_to_validate") && (
        <FormField label="Phone number" error={errors.phone_number_to_validate}>
          <PhoneField
            label=""
            width="100%"
            onChange={(e) =>
              onChangeRegisterInfo({
                phone_number_to_validate: e.target.value,
              })
            }
            value={sparkRegisterInfo.phone_number_to_validate || ""}
          />
        </FormField>
      )}

      {!omitFields?.includes("phone_number_to_validate") && (
        <TextNotificationOptIn
          checked={textOptIn}
          onChange={onChangeTextOptIn}
        />
      )}

      {policyCheckboxes}
      </Box>
      <Box
        flexShrink={0}
        px="20px"
        pt="12px"
        pb="calc(12px + env(safe-area-inset-bottom))"
        bg="bg.canvas"
        borderTop="1px solid"
        borderColor="border.subtle"
      >
        <Button size="2xl" w="100%" onClick={submit} disabled={isLoading}>
          {buttonLabel}
        </Button>
      </Box>
    </Box>
  );
};
